/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'@waca/dashboard-common/js/core/APIFactory',
	'./ZoomAPI',
	'./ZoomAPISpec',
	'@waca/dashboard-common/js/api/ContentActionsProviderAPI',
	'@waca/dashboard-common/js/api/PropertiesProviderAPI',
	'../../../lib/@waca/core-client/js/core-client/i18n/Formatter',
	'../../../app/nls/StringResources',
	'resize-observer-polyfill'
], function(APIFactory, ZoomAPI, ZoomAPISpec, ContentActionsProviderAPI, PropertiesProviderAPI, Formatter, stringResources, ResizeObserver) {

	const CONTAINER_PADDING = 24;

	const DEFAULT_ZOOM_VALUE = '1';

	const ZOOM_VALUES = [
		{
			id: '2',
			localizedString: Formatter.formatNumber('2', { type: 'percent' })
		},{
			id: '1.5',
			localizedString: Formatter.formatNumber('1.5', { type: 'percent' })
		},{
			id: '1',
			localizedString: Formatter.formatNumber('1', { type: 'percent' })
		}, {
			id: '0.75',
			localizedString: Formatter.formatNumber('0.75', { type: 'percent' })
		},{
			id: '0.5',
			localizedString: Formatter.formatNumber('0.5', { type: 'percent' })
		}];

	class Zoom extends ZoomAPISpec {
		constructor(options) {
			super(options);

			this._features = options.features;
			this._dashboardState = this._features.DashboardState;
			this._api = APIFactory.createAPI(this, [ZoomAPI, ContentActionsProviderAPI, PropertiesProviderAPI]);
			this._eventHandlers = [];
			this._updateZoomLevel = this._updateZoomLevel.bind(this);
			this._onWindowResize = this._onWindowResize.bind(this);
			const featureChecker = this._features.API.getGlassCoreSvc('.FeatureChecker');
			this._isPropertiesV2Enabled = featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');

			this._features.API.getCanvasWhenReady().then(() => {
				// Monitor the mode change and disable the zoom in edit mode.
				this._features.DashboardState.onUiStateChange(this._updateZoomLevel);

				this._canvas = this._features.Canvas;
				this._content = this._canvas.getContent();
				this._features['Properties'].registerProvider(this.getAPI());
				this._eventHandlers.push(this._canvas.on('change:selections:select', this._onSelectionChange, this));
				this._eventHandlers.push(this._canvas.on('change:property:layoutPositioning', this._updateZoomLevel, this));
				this._eventHandlers.push(this._canvas.on('change:property:defaultZoom', this._onDefaultZoomLevelChange, this));
				this._eventHandlers.push(this._features.API.on('enter:fullscreen', this._onFullScreenEnter, this));
				this._eventHandlers.push(this._features.API.on('exit:fullscreen', this._onFullScreenExit, this));
				this._observer = new ResizeObserver(this._onWindowResize);
				this._observer.observe(this._getSelectedPage().getFeature('ContentViewDOM').getNode());
				this._currZoomOption = this._canvas.getPropertyValue('defaultZoom');
				if (this._isZoomEnabled() && !this._isAuthoring()) {
					this._setZoomModeOrZoomLevel(this._currZoomOption);
				}
			});

			options.features.ContentActions.registerProvider('zoomProperty', this._api);
		}

		destroy() {
			this._observer.disconnect();
			this._dashboardState.offUiStateChange(this._updateZoomLevel);
			this._eventHandlers.forEach(handler => {
				if (handler){
					handler.remove();
				}
			});
			this._content = null;
		}

		getAPI() {
			return this._api;
		}

		getPropertyList() {
			let sectionId = 'general.canvas.zoom';
			if (this._isPropertiesV2Enabled) {
				sectionId = 'canvas.zoom';
			}
			return [{
				'id': 'defaultZoom',
				supportsUndoRedo: true,
				validatePropertyValue: (value) => {
					return {
						isValid: this._getZoomOptions().map(item => item.id).includes(value),
						message: 'default zoom value is invalid.'
					};
				},
				defaultValue: '1',
				'editor': {
					'sectionId': sectionId,
					'uiControl': {
						'type': 'DropDown',
						'label': stringResources.get('defaultZoomValue'),
						'disabled': !this._isZoomEnabled(),
						'ariaLabel': stringResources.get('defaultZoomValue'),
						'options': this._getZoomOptions().map(e => {
							return {
								label: e.localizedString,
								value: e.id
							};
						})
					}
				}
			}];
		}

		getPropertyLayoutList() {
			return [{
				'type': 'Section',
				'id': 'zoom',
				'collapsible': false,
				'label': stringResources.get('zoomCategoryLabel'),
				position: 2
			}];
		}

		getContentActionList(){
			// Do not return action list if user is in authoring mode
			if (this._isAuthoring()) {
				return [];
			}

			return [{
				name: 'zoomAction',
				label: 'Zoom',
				defaultValue: DEFAULT_ZOOM_VALUE,
				icon: this._features.Icons.getIcon('zoomIn').id,
				type: 'NextView',
				disabled: !this._isZoomEnabled(),
				section: 700,
				order: 1,
				view: {
					module: 'dashboard-core/js/lib/@waca/dashboard-common/dist/ui/dialogs/CommonSelectItemsDialog',
					state: this._getViewState()
				},
				actions: {
					apply: this._zoom.bind(this)
				},
				text: this._getSelectedItemCaption(),
				className: 'buttonWithText',
				callbacks: {
					registerCallback: (button) => {
						this._button = button;
					},
					unregisterCallback: () => {
						this._button = null;
					}
				}
			}];
		}

		_isAuthoring(){
			return this._features.DashboardState.getUiState().authoring;
		}

		_onWindowResize() {
			if (this._isZoomModeAutomatic(this._currZoomOption)) {
				this.getAPI().setZoomMode(this._currZoomOption);
			} else if (this._zoomRatio < 1) {
				const pageContent = this._getSelectedPage();
				this._applyZoomLevel(pageContent);
			}
		}

		_onFullScreenEnter() {
			this._preFullScreenZoomLevel = this._currZoomOption;
			if (this._isZoomEnabled() && !this._isAuthoring()) {
				this.getAPI().setZoomMode('fitPage');
			} else {
				this._currZoomOption = 'fitPage';
			}
		}

		_onFullScreenExit() {
			this._currZoomOption = this._preFullScreenZoomLevel;
			this._setZoomModeOrZoomLevel(this._currZoomOption);
			if (this._button) {
				this._button.updateState({ disabled:  !this._isZoomEnabled(), text: this._getSelectedItemCaption() });
			}
		}

		_updateZoomLevel() {
			const zoomEnabled = this._isZoomEnabled();
			if (zoomEnabled && !this._isAuthoring()) {
				this._setZoomModeOrZoomLevel(this._currZoomOption);
			} else {
				this._removeZoomStyling();
			}
			if (this._button) {
				this._button.updateState({ disabled:  !zoomEnabled, text: this._getSelectedItemCaption() });
			}
		}

		_onSelectionChange(event) {
			const id = event.info.value[0];
			const content = this._canvas.getContent(id);
			// we are only monitoring the page under the top layout tabs ..
			// once we switch to a new page, apply the previously set zoom ratio to it.
			if (content && content.getType() === 'page' && content.getContainer() === this._content){
				content.getFeature('state').whenStatusChanges('rendered').then(() => {
					this._observer.disconnect();
					this._observer.observe(content.getFeature('ContentViewDOM').getNode());
					this._applyZoomLevel(content);
				});
			}
		}

		// If there is not a selected page (infographic) fall back on the canvas content
		_getSelectedPage() {
			return this._canvas.getSelectedContentList({ type: 'page' })[0] || this._content;
		}

		_calculateZoomRatio(pageContent) {
			let zoomRatio = 1;
			if (pageContent && this._isZoomEnabled() && !this._isAuthoring()){
				const zoom = this.getZoomMode();
				if (zoom === ZoomAPI.ZOOM_MODES.MANUAL) {
					return this._zoomRatio;
				}
				const clientNode = pageContent.getFeature('ContentViewDOM').getNode();

				const pageWidth = this._content.getPropertyValue('pageSizeWidth') + CONTAINER_PADDING; //add the container 24 px padding

				if (zoom === ZoomAPI.ZOOM_MODES.FIT_WIDTH) {
					zoomRatio = clientNode.clientWidth / pageWidth;
				} else if (zoom === ZoomAPI.ZOOM_MODES.FIT_PAGE) {
					const horizScrollBarHeight = clientNode.offsetHeight - clientNode.clientHeight;
					const pageHeight = this._content.getPropertyValue('pageSizeHeight') + CONTAINER_PADDING; //add the container 24 px padding
					const clientNodeHeight = clientNode.clientHeight - horizScrollBarHeight - (CONTAINER_PADDING / 2);

					zoomRatio = Math.min(clientNode.clientWidth / pageWidth, clientNodeHeight / pageHeight);
				}
			}
			return zoomRatio;
		}

		_applyZoomLevel(pageContent) {
			if (!this._zoomRatio) {
				return;
			}

			const contentView = pageContent.getFeature('ContentViewDOM');
			if (contentView) {
				const clientNode = contentView.getNode();
				const node = clientNode.firstElementChild;
				node.classList.add('pageZoomContainer');
				node.classList.remove('centreContent');
				node.classList.remove('fitContent');
				if (this._zoomRatio === 1 && !this._isZoomModeAutomatic(this.getZoomMode()) || this._isAuthoring() || !this._isZoomEnabled()){
					// 100% zoom -- clear all zoom related styles
					node.style.transform = null;
					return;
				}
				const zoom = this._currZoomOption;
				if (zoom === 'fitPage'){
					node.style.transform = `scale(${this._zoomRatio}) translateX(-50%)`;
					node.classList.add('centreContent');
				} else if (this._zoomRatio < 1){
					const pageWidth = this._content.getPropertyValue('pageSizeWidth') + CONTAINER_PADDING;
					if (pageWidth * this._zoomRatio <= clientNode.clientWidth) {
						node.style.transform = `scale(${this._zoomRatio}) translateX(-50%)`;
						node.classList.add('centreContent');
					} else {
						node.style.transform = `scale(${this._zoomRatio})`;
						node.classList.add('fitContent');
					}
				} else {
					node.style.transform = `scale(${this._zoomRatio})`;
				}
			}
		}

		_removeZoomStyling() {
			const pageContent = this._getSelectedPage();
			if (pageContent) {
				const clientNode = pageContent.getFeature('ContentViewDOM').getNode();
				const node = clientNode.firstElementChild;
				node.classList.remove('pageZoomContainer');
				node.classList.remove('fitPage');
				node.style.transform = null;
				this.setZoomLevel(1);
			}
		}

		_isZoomEnabled() {
			const renderer = this._features['Renderer.internal'];
			return this._content.getPropertyValue('layoutPositioning') === 'absolute' &&
				!this._features.DashboardState.getUiState().focus &&
				!(renderer && renderer.isMobile());
		}

		_isZoomModeAutomatic(mode) {
			return Object.values(ZoomAPI.ZOOM_MODES).includes(mode) && mode !== ZoomAPI.ZOOM_MODES.MANUAL;
		}

		_setZoomModeOrZoomLevel(value) {
			if (this._isZoomModeAutomatic(value)) {
				this.getAPI().setZoomMode(value);
			} else {
				this.getAPI().setZoomLevel(parseFloat(value));
			}
		}

		_zoom(value) {
			this._currZoomOption = value;
			if (this._isZoomModeAutomatic(value)) {
				this.setZoomMode(value);
			} else {
				this.getAPI().setZoomLevel(parseFloat(value));
			}
			if(this._button) {
				this._button.updateState({ disabled:  !this._isZoomEnabled(), text: this._getSelectedItemCaption() });
			}
		}

		_getViewState() {
			return {
				height: 140,
				width: 140, // those are hints only; for the placement of the toolbar.
				getCurrentSelectedItem: this._getSelectedZoomOption.bind(this),
				possibleItems: this._getZoomOptions()
			};
		}

		_getZoomOptions() {
			return [
				{
					id: ZoomAPI.ZOOM_MODES.FIT_WIDTH,
					localizedString: stringResources.get('zoomFitToWidth')
				},
				{
					id: ZoomAPI.ZOOM_MODES.FIT_PAGE,
					localizedString: stringResources.get('zoomWholePage')
				}
			].concat(ZOOM_VALUES);
		}

		_getSelectedItemCaption () {
			if (!this._isZoomEnabled()) {
				return Formatter.formatNumber('1', { type: 'percent' });
			}
			const selected =  this._getZoomOptions().find(item => item.id === String(this._currZoomOption));
			return selected && selected.localizedString;
		}

		_getSelectedZoomOption() {
			return this._currZoomOption;
		}

		_onDefaultZoomLevelChange(event) {
			if (this._features.DashboardState.getUiState().fullScreen) {
				this._preFullScreenZoomLevel = event.info.value;
			} else {
				this._currZoomOption = event.info.value;
				this._updateZoomLevel();
			}
		}

		getZoomLevel () {
			return this._zoomRatio;
		}

		getZoomMode() {
			if (this._features.DashboardState.getUiState().focus) {
				return ZoomAPI.ZOOM_MODES.MANUAL;
			}
			return this._isZoomModeAutomatic(this._currZoomOption) ? this._currZoomOption : ZoomAPI.ZOOM_MODES.MANUAL;
		}

		setZoomMode(mode) {
			if (this._isZoomEnabled() && !this._isAuthoring()) {
				const pageContent = this._getSelectedPage();
				if (this._isZoomModeAutomatic(mode)) {
					this._currZoomOption = mode;
					const newRatio = this._calculateZoomRatio(pageContent);
					this.getAPI().setZoomLevel(parseFloat(newRatio));
					// Need to reapply zoom option, zoom mode, and button state
					this._currZoomOption = mode;
					this._applyZoomLevel(pageContent);
					if (this._button) {
						this._button.updateState({ disabled:  !this._isZoomEnabled(), text: this._getSelectedItemCaption() });
					}
				} else {
					this._features.Logger.error(`${mode} is not a valid zoom mode.`);
				}
			}
		}

		setZoomLevel(value) {
			if (this._isZoomEnabled() && !this._isAuthoring()) {
				const pageContent = this._getSelectedPage();
				// Ensure zoom value is one of the predefined zoom values
				if (ZOOM_VALUES.map(x => x.id).includes(String(value))) {
					this._currZoomOption = String(value);
					if (this._button) {
						this._button.updateState({ disabled:  !this._isZoomEnabled(), text: this._getSelectedItemCaption() });
					}
				}
				this._zoomRatio = value;
				this._applyZoomLevel(pageContent);
			} else {
				this._zoomRatio = 1;
			}
		}
	}

	return Zoom;

});
